import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addMonths } from 'date-fns';
import {
  Calendar,
  Clock,
  FileText
} from 'lucide-react';
import { useGoals } from '../../hooks/useGoals';
import ModalWrapper from '../ui/ModalWrapper';
import WizardHeader from '../ui/WizardHeader';
import WizardProgress from '../ui/WizardProgress';
import WizardError from '../ui/WizardError';
import WizardContent from '../ui/WizardContent';
import DurationStep from '../ui/DurationStep';
import MotionStep from '../ui/MotionStep';
import DatesStep from '../ui/DatesStep';
import DetailsStep from '../ui/DetailsStep';
import WizardFooter from '../ui/WizardFooter';
import { useTranslation } from 'react-i18next';
import FancyLoader from '../FancyLoader';

export interface WizardStep {
  title: string;
  description: string;
  icon: React.ElementType;
}

// Añadimos el callback onGoalCreated a las props
export interface GoalWizardProps {
  onClose: () => void;
  onGoalCreated?: () => void; // Callback opcional para actualizar la página
}

export function GoalWizard({ onClose, onGoalCreated }: GoalWizardProps) {
  const { t } = useTranslation();

  // Define STEPS using t function for translations
  const STEPS: WizardStep[] = [
    {
      title: t('goals.wizard.steps.duration.title'),
      description: t('goals.wizard.steps.duration.description'),
      icon: Clock
    },
    {
      title: t('goals.wizard.steps.dates.title'),
      description: t('goals.wizard.steps.dates.description'),
      icon: Calendar
    },
    {
      title: t('goals.wizard.steps.details.title'),
      description: t('goals.wizard.steps.details.description'),
      icon: FileText
    }
  ];

  const DURATIONS = {
    short: {
      label: t('goals.wizard.durations.short.label'),
      months: 1,
      description: t('goals.wizard.durations.short.description'),
      color: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
    },
    medium: {
      label: t('goals.wizard.durations.medium.label'),
      months: 3,
      description: t('goals.wizard.durations.medium.description'),
      color: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
    },
    long: {
      label: t('goals.wizard.durations.long.label'),
      months: 12,
      description: t('goals.wizard.durations.long.description'),
      color: 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800'
    }
  };

  type Duration = keyof typeof DURATIONS;

  const [step, setStep] = useState(0);
  const [duration, setDuration] = useState<Duration>('short');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(addMonths(new Date(), 1));
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);

  const { createGoal } = useGoals();

  const validateStep = () => {
    switch (step) {
      case 1:
        if (startDate >= endDate) {
          setValidationError(t('goals.wizard.errors.dates.startAfterEnd'));
          return false;
        }
        break;
      case 2:
        if (!title.trim()) {
          setValidationError(t('goals.wizard.errors.details.emptyTitle'));
          return false;
        }
        if (!description.trim()) {
          setValidationError(t('goals.wizard.errors.details.emptyDescription'));
          return false;
        }
        break;
    }
    setValidationError('');
    return true;
  };

  const handleDurationSelect = (newDuration: Duration) => {
    setDuration(newDuration);
    setEndDate(addMonths(startDate, DURATIONS[newDuration].months));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    try {
      setIsSubmitting(true);
      await createGoal({
        title,
        description,
        startDate,
        endDate,
        duration
      });

      setShowSuccess(true);

      setTimeout(() => {
        if (onGoalCreated) {
          onGoalCreated();
        }

        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error creating goal:', error);
      setValidationError(t('goals.wizard.errors.submission'));
      setIsSubmitting(false);
    }
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setStep(Math.min(STEPS.length - 1, step + 1));
    }
  };

  const handlePreviousStep = () => {
    setStep(Math.max(0, step - 1));
  };

  const getLoaderTheme = (): 'primary' | 'success' | 'warning' | 'danger' => {
    switch (duration) {
      case 'short':
        return 'success';
      case 'medium':
        return 'primary';
      case 'long':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <WizardHeader onClose={onClose} title={t('goals.wizard.title')} />

      <WizardProgress currentStep={step} steps={STEPS} />

      <WizardError error={validationError} />

      <WizardContent>
        {isSubmitting ? (
          <div className="flex flex-col items-center justify-center py-8">
            <FancyLoader
              message={showSuccess
                ? t('goals.wizard.success')
                : t('goals.wizard.submitting')}
              size="md"
              theme={showSuccess ? "success" : getLoaderTheme()}
              showBackground={true}
              showProgressDots={!showSuccess}
            />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {step === 0 && (
              <MotionStep key="duration">
                <DurationStep
                  key={startDate.toString()}
                  selectedDuration={duration}
                  onSelectDuration={handleDurationSelect}
                  startDate={startDate}
                  DURATIONS={DURATIONS}
                />
              </MotionStep>
            )}

            {step === 1 && (
              <MotionStep key="dates">
                <DatesStep
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={(date) => {
                    setStartDate(date);
                    setEndDate(addMonths(date, DURATIONS[duration].months));
                  }}
                  onEndDateChange={setEndDate}
                  duration={duration}
                />
              </MotionStep>
            )}

            {step === 2 && (
              <MotionStep key="details">
                <DetailsStep
                  title={title}
                  description={description}
                  onTitleChange={setTitle}
                  onDescriptionChange={setDescription}
                />
              </MotionStep>
            )}
          </AnimatePresence>
        )}
      </WizardContent>

      <WizardFooter
        currentStep={step}
        totalSteps={STEPS.length}
        onPrevious={handlePreviousStep}
        onNext={handleNextStep}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </ModalWrapper>
  );
}